import { useEffect, useMemo } from 'react';
import { Table } from '@/components';
import {
  Delete as DeleteIcon,
  NotInterested as NotInterestedIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTaskActions, useTasks, useTasksPagination } from '../hooks';
import { tasksListColumns } from '../config';
import { TaskFilter } from '../interfaces';
import { IconButton, Tooltip } from '@mui/material';
import { TaskStatus } from '../enums';

interface TaskTableProps {
  filter: TaskFilter;
}

const TaskTable = ({ filter }: TaskTableProps) => {
  const filterKey = useMemo(
    () =>
      [
        filter.name?.trim() ?? '',
        [...filter.methods].sort().join(','),
        [...filter.statuses].sort().join(','),
      ].join('|'),
    [filter]
  );

  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useTasksPagination({ filterKey });

  const { onDeleteTask, onUpdateTask } = useTaskActions();

  const { tasks, totalItems, totalPages, isLoading, isFetching } = useTasks({
    filter,
    paginated: {
      page: currentPage,
      pageSize: pageSize,
    },
  });

  // Si se borran tareas o cambia el tamaño de página podemos quedar parados en
  // una página que ya no existe; en ese caso volvemos a la última con datos.
  useEffect(() => {
    if (isFetching) return;
    if (totalPages > 0 && currentPage > totalPages) {
      handlePageChange(totalPages);
    }
  }, [isFetching, totalPages, currentPage, handlePageChange]);

  const columns = useMemo(() => tasksListColumns, []);

  const renderActions = (rowData: Record<string, any>) => {
    const { id, status } = rowData;

    return (
      <>
        {status === TaskStatus.ACTIVE && (
          <Tooltip placement='top' title='Desactivar'>
            <IconButton onClick={() => onUpdateTask(id, status)}>
              <NotInterestedIcon />
            </IconButton>
          </Tooltip>
        )}
        {status === TaskStatus.INACTIVE && (
          <Tooltip placement='top' title='Activar'>
            <IconButton onClick={() => onUpdateTask(id, status)}>
              <CheckIcon />
            </IconButton>
          </Tooltip>
        )}
        {[TaskStatus.ACTIVE, TaskStatus.INACTIVE].includes(status) && (
          <Tooltip placement='top' title='Eliminar'>
            <IconButton onClick={() => onDeleteTask(id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      <Table
        data={tasks}
        columns={columns}
        isLoading={isLoading || isFetching}
        renderActions={renderActions}
        getRowKey={(rowData) => String(rowData.id)}
        pagination={{
          totalItems,
          totalPages,
          currentPage,
          rowsPerPage: pageSize,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handlePageSizeChange,
        }}
      ></Table>
    </div>
  );
};

export default TaskTable;
